import { Modal, TextInput, Button, Group, Stack, Textarea, Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useCreateTeam } from '../../services/useTeamQueries';
import { notifications } from '@mantine/notifications';
import { useTeamStore } from '@/stores/teamStore';
import { useWorkspaceStore } from '@/features/workspace/hooks/workspaceStore';

interface CreateTeamDialogProps {
    opened: boolean;
    onClose: () => void;
    initialWorkspaceId?: string | null;
}

export default function CreateTeamDialog({ opened, onClose, initialWorkspaceId = null }: CreateTeamDialogProps) {
    const { t } = useTranslation('team');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [workspaceId, setWorkspaceId] = useState<string | null>(initialWorkspaceId);
    const [nameError, setNameError] = useState<string | null>(null);
    const [workspaceError, setWorkspaceError] = useState<string | null>(null);
    const createTeamMutation = useCreateTeam();
    const { teams, setTeams } = useTeamStore();
    const { workspaces, fetchWorkspaces } = useWorkspaceStore();

    useEffect(() => {
        if (opened) {
            void fetchWorkspaces();
            // 대화상자가 열릴 때마다 initialWorkspaceId 적용
            if (initialWorkspaceId) {
                setWorkspaceId(initialWorkspaceId);
            }
        }
    }, [opened, fetchWorkspaces, initialWorkspaceId]);

    const validateName = (value: string) => {
        if (value.length < 2) {
            setNameError(t('create_dialog.name_error'));
            return false;
        }
        setNameError(null);
        return true;
    };

    const validateWorkspace = () => {
        if (!workspaceId) {
            setWorkspaceError(t('create_dialog.workspace_required'));
            return false;
        }
        setWorkspaceError(null);
        return true;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateName(name) || !validateWorkspace()) {
            return;
        }

        createTeamMutation.mutate({
            name,
            description,
            profileImageUrl: null,
            workspaceId: workspaceId,
            }, {
                onSuccess: (newTeam) => {
                    // 새로 생성된 팀을 스토어에 추가
                    setTeams([...teams, newTeam]);
                    
                    notifications.show({
                        title: t('create_dialog.success_title'),
                        message: t('create_dialog.success_message', { name }),
                        color: 'green',
                    });
                    setName('');
                    setDescription('');
                    setWorkspaceId(null);
                    onClose();
                },
                onError: (error) => {
                    notifications.show({
                        title: t('create_dialog.error_title'),
                        message: t('create_dialog.error_message', { error }),
                        color: 'red',
                    });
                }
            }
        );
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('create_dialog.title')}
            size="md"
            centered
        >
            <form onSubmit={handleSubmit}>
                <Stack gap="md">
                    <Select
                        required
                        label={t('create_dialog.workspace')}
                        placeholder={t('create_dialog.select_workspace')}
                        data={workspaces.map(workspace => ({
                            value: workspace.id,
                            label: workspace.name
                        }))}
                        value={workspaceId}
                        onChange={(value) => {
                            setWorkspaceId(value);
                            if (value) setWorkspaceError(null);
                        }}
                        error={workspaceError}
                        data-autofocus
                    />
                    <TextInput
                        required
                        label={t('create_dialog.team_name')}
                        placeholder={t('create_dialog.enter_team_name')}
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            validateName(e.target.value);
                        }}
                        error={nameError}
                    />
                    <Group justify="flex-end" gap="sm">
                        <Button variant="subtle" onClick={onClose}>
                            {t('create_dialog.cancel')}
                        </Button>
                        <Button type="submit" loading={createTeamMutation.isPending}>
                            {t('create_dialog.create_team')}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
}
